<script setup lang="ts">
/**
 * MInvoiceDocument — Reusable invoice document component.
 *
 * Renders the invoice body in three modes:
 *   - "view"   — read-only display (overview page)
 *   - "create" — editable form for new invoices
 *   - "edit"   — editable form for existing invoices
 *
 * The parent page provides the surrounding chrome (action bars, sidebars, buttons).
 * This component handles only the paper-like invoice body.
 *
 * Exposes validate() for the parent to call before submitting.
 */

import { ref, watch } from "vue";

interface CatalogProduct {
  id: string;
  name: string;
  categoryName: string | null;
  defaultUnitPrice: number;
  defaultUnitPriceFormatted: string;
  vatStatus: string;
  vatStatusLabel: string;
}

const props = defineProps<{
  mode: "view" | "create" | "edit";

  // Invoice header (always required)
  invoiceNumber?: string | null;
  patientName?: string | null;
  payeeAccountName?: string | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  notes?: string | null;

  // Line items — view mode uses pre-formatted strings
  lineItems: Array<{
    description: string;
    unitPriceFormatted?: string;
    unitPrice?: number;
    quantity: number;
    vatStatusLabel?: string;
    vatStatus?: string;
    lineTotalFormatted?: string;
    productId?: string | null;
  }>;

  // Totals — view mode
  subtotalFormatted?: string | null;
  totalFormatted?: string | null;

  // Create/edit mode form inputs (passed via v-model:xxx)
  // Patient selection (create only)
  payeeAccountOptions?: Array<{ label: string; value: string }>;
  patientId?: string | null;
  payeeAccountId?: string | null;

  // Products for line item modal
  products?: CatalogProduct[];
}>();

const emit = defineEmits<{
  (e: "update:patientId", value: string): void;
  (e: "update:patientName", value: string): void;
  (e: "update:payeeAccountId", value: string): void;
  (e: "update:invoiceDate", value: string): void;
  (e: "update:dueDate", value: string): void;
  (e: "update:notes", value: string): void;
  (e: "selectPatient"): void;
  (e: "addLineItem"): void;
  (e: "removeLineItem", index: number): void;
  (e: "addPayeeAccount"): void;
}>();

// --- Date validation & constraints ---
// m-date-input silently preserves old model values when the user enters an
// invalid date (e.g. June 31). It does mark the invalid sub-fields with the
// CSS class .input-error. We check the DOM for this class to detect invalidity.
//
// Due date must be >= invoice date. If the user moves invoice date past the
// current due date, we auto-reset due date to match.

const validationErrors = ref<string[]>([]);
const invoiceDateEl = ref<{ $el?: HTMLElement } | null>(null);
const dueDateEl = ref<{ $el?: HTMLElement } | null>(null);

function hasInputError(componentRef: { $el?: HTMLElement } | null): boolean {
  if (!componentRef) return false;
  const el = componentRef.$el || (componentRef as unknown as HTMLElement);
  if (!el || typeof el.querySelector !== "function") return false;
  return el.querySelector(".input-error") !== null;
}

// When invoice date changes past the current due date, reset due date to match
watch(
  () => props.invoiceDate,
  (newInvoiceDate) => {
    if (!newInvoiceDate || props.mode === "view") return;
    if (!props.dueDate || newInvoiceDate > props.dueDate) {
      emit("update:dueDate", newInvoiceDate);
    }
  },
);

/**
 * Validates the document fields. Returns true if valid.
 * Call this from the parent page before submitting.
 */
function validate(): boolean {
  const errors: string[] = [];

  if (props.mode === "create" && !props.patientId) {
    errors.push("Please select a patient.");
  }
  if (!props.payeeAccountId) {
    errors.push("Please select a payee account.");
  }
  if (!props.invoiceDate) {
    errors.push("Please enter an invoice date.");
  } else if (hasInputError(invoiceDateEl.value)) {
    errors.push("Invoice date is not a valid date.");
  }
  if (!props.dueDate) {
    errors.push("Please enter a due date.");
  } else if (hasInputError(dueDateEl.value)) {
    errors.push("Due date is not a valid date.");
  } else if (props.invoiceDate && props.dueDate < props.invoiceDate) {
    errors.push("Due date cannot be before the invoice date.");
  }
  if (props.lineItems.length === 0) {
    errors.push("Please add at least one line item.");
  }

  validationErrors.value = errors;
  return errors.length === 0;
}

defineExpose({ validate });

function formatMoney(amount: number): string {
  return "£" + Number(amount).toFixed(2);
}

function computedSubtotal(): number {
  return props.lineItems.reduce((sum, item) => sum + (item.unitPrice || 0) * item.quantity, 0);
}

const isView = () => props.mode === "view";
const isEditable = () => props.mode === "create" || props.mode === "edit";
const showInvoiceNumber = () => props.mode === "view" || props.mode === "edit";
</script>

<template>
  <div class="invoice-doc">
    <!-- Validation errors — shown when validate() is called with failures -->
    <div v-if="validationErrors.length > 0" class="invoice-doc__errors" role="alert">
      <div v-for="error in validationErrors" :key="error" class="invoice-doc__error">
        {{ error }}
      </div>
    </div>
    <!-- Invoice Number — shown at top for view and edit modes -->
    <template v-if="showInvoiceNumber()">
      <div class="invoice-doc__fields">
        <div class="invoice-doc__field">
          <div class="invoice-doc__label">Invoice Number</div>
          <div class="invoice-doc__value" :class="{ 'invoice-doc__value--muted': !invoiceNumber }">
            {{ invoiceNumber || "DRAFT" }}
          </div>
        </div>
        <div class="invoice-doc__field"></div>
      </div>
      <hr class="invoice-doc__divider" />
    </template>

    <h3 class="invoice-doc__section-title">Invoice Details</h3>

    <!-- Row 1: Patient / Bill To -->
    <div class="invoice-doc__fields">
      <div class="invoice-doc__field">
        <div class="invoice-doc__label">Patient {{ mode === "create" ? "*" : "" }}</div>
        <!-- View / Edit: read-only patient name -->
        <div v-if="isView() || mode === 'edit'" class="invoice-doc__value">
          {{ patientName }}
        </div>
        <!-- Create: patient picker -->
        <div v-else class="invoice-doc__patient-picker">
          <div class="invoice-doc__value" :class="{ 'invoice-doc__value--muted': !patientName }">
            {{ patientName || "No patient selected" }}
          </div>
          <m-button label="Select Patient" color="secondary" @click="emit('selectPatient')" />
        </div>
      </div>
      <div class="invoice-doc__field">
        <div class="invoice-doc__label-row">
          <div class="invoice-doc__label">Bill To *</div>
          <m-button
            v-if="isEditable() && patientId"
            label="+ Add"
            ghost
            size="xs"
            @click="emit('addPayeeAccount')"
          />
        </div>
        <!-- View: static text -->
        <div v-if="isView()" class="invoice-doc__value">
          {{ payeeAccountName }}
        </div>
        <!-- Create/Edit: no patient selected yet -->
        <div
          v-else-if="mode === 'create' && !patientId"
          class="invoice-doc__value invoice-doc__value--muted"
        >
          Select a patient first
        </div>
        <!-- Create/Edit: patient selected but no payee accounts -->
        <div
          v-else-if="isEditable() && patientId && (payeeAccountOptions || []).length === 0"
          class="invoice-doc__value invoice-doc__value--muted"
        >
          No payee accounts — use + Add above
        </div>
        <!-- Create/Edit: dropdown -->
        <m-simple-select
          v-else
          :model-value="payeeAccountId"
          :options="payeeAccountOptions || []"
          placeholder="Select payee account"
          @update:model-value="emit('update:payeeAccountId', $event)"
        />
      </div>
    </div>

    <hr class="invoice-doc__divider" />

    <!-- Row 2: Invoice Date / Due Date — always shown -->
    <div class="invoice-doc__fields">
      <div class="invoice-doc__field">
        <template v-if="isView()">
          <div class="invoice-doc__label">Invoice Date</div>
          <div class="invoice-doc__value">{{ invoiceDate || "—" }}</div>
        </template>
        <m-date-input
          v-else
          ref="invoiceDateEl"
          label="Invoice Date"
          required
          :model-value="invoiceDate"
          @update:model-value="emit('update:invoiceDate', $event)"
        />
      </div>
      <div class="invoice-doc__field">
        <template v-if="isView()">
          <div class="invoice-doc__label">Due Date</div>
          <div class="invoice-doc__value">{{ dueDate || "—" }}</div>
        </template>
        <m-date-input
          v-else
          ref="dueDateEl"
          label="Due Date"
          required
          :model-value="dueDate"
          :min-date="invoiceDate || undefined"
          min-date-text="Due date cannot be before the invoice date."
          @update:model-value="emit('update:dueDate', $event)"
        />
      </div>
    </div>

    <!-- Notes -->
    <template v-if="isView() ? !!notes : true">
      <hr class="invoice-doc__divider" />
      <div class="invoice-doc__field">
        <div class="invoice-doc__label">{{ isView() ? "Notes" : "Internal Notes" }}</div>
        <div v-if="isView()" class="invoice-doc__value invoice-doc__value--muted">
          {{ notes }}
        </div>
        <m-input
          v-else
          :model-value="notes"
          type="textarea"
          placeholder="Notes (not shown on invoice)"
          maxlength="2000"
          @update:model-value="emit('update:notes', $event)"
        />
      </div>
    </template>

    <hr class="invoice-doc__divider invoice-doc__divider--section" />

    <!-- Line Items -->
    <div class="invoice-doc__line-items-header">
      <h3 class="invoice-doc__section-title" style="margin-bottom: 0">Line Items</h3>
      <m-button v-if="isEditable()" label="+ Add line item" ghost @click="emit('addLineItem')" />
    </div>

    <!-- Empty state -->
    <div v-if="lineItems.length === 0" class="invoice-doc__empty">
      {{
        isEditable()
          ? "No line items added yet. Add items from your product catalogue."
          : "No line items."
      }}
    </div>

    <!-- Line items table -->
    <table v-else class="invoice-doc__table">
      <thead>
        <tr>
          <th class="invoice-doc__th">Description</th>
          <th class="invoice-doc__th invoice-doc__th--right">Unit Price</th>
          <th class="invoice-doc__th invoice-doc__th--center">Qty</th>
          <th class="invoice-doc__th">VAT</th>
          <th class="invoice-doc__th invoice-doc__th--right">Total</th>
          <th v-if="isEditable()" class="invoice-doc__th invoice-doc__th--actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in lineItems" :key="index">
          <td class="invoice-doc__td">{{ item.description }}</td>
          <td class="invoice-doc__td invoice-doc__td--right">
            {{ item.unitPriceFormatted || formatMoney(item.unitPrice || 0) }}
          </td>
          <td class="invoice-doc__td invoice-doc__td--center">{{ item.quantity }}</td>
          <td class="invoice-doc__td">
            {{ item.vatStatusLabel || item.vatStatus }}
          </td>
          <td class="invoice-doc__td invoice-doc__td--right">
            {{ item.lineTotalFormatted || formatMoney((item.unitPrice || 0) * item.quantity) }}
          </td>
          <td v-if="isEditable()" class="invoice-doc__td invoice-doc__td--actions">
            <m-button
              icon="fa-solid fa-trash"
              ghost
              sr-label="Remove item"
              @click="emit('removeLineItem', index)"
            />
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td :colspan="isEditable() ? 3 : 3" class="invoice-doc__tfoot-empty"></td>
          <td class="invoice-doc__tfoot-label">Subtotal</td>
          <td class="invoice-doc__tfoot-value">
            {{ subtotalFormatted || formatMoney(computedSubtotal()) }}
          </td>
          <td v-if="isEditable()" class="invoice-doc__tfoot-empty"></td>
        </tr>
        <tr>
          <td :colspan="isEditable() ? 3 : 3" class="invoice-doc__tfoot-empty"></td>
          <td class="invoice-doc__tfoot-label invoice-doc__tfoot-label--total">Total</td>
          <td class="invoice-doc__tfoot-value invoice-doc__tfoot-value--total">
            {{ totalFormatted || formatMoney(computedSubtotal()) }}
          </td>
          <td v-if="isEditable()" class="invoice-doc__tfoot-empty"></td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<style>
/* Invoice document — styled like a sheet of paper.
   Intentionally NOT scoped — uses .invoice-doc namespace to prevent collisions.
   This CSS is shared between the Vue component and future PDF templates. */

.invoice-doc {
  background: #ffffff;
  border-radius: 4px;
  box-shadow: var(--box-shadow, 0 1px 3px rgba(0, 0, 0, 0.1));
  padding: 28px 32px;
}

.invoice-doc__section-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-color, #1f2937);
  margin: 0 0 16px 0;
}

.invoice-doc__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 48px;
}

.invoice-doc__field {
  min-width: 0;
}

.invoice-doc__label {
  font-size: 13px;
  color: var(--grey-darkest, #6b7280);
  margin-bottom: 6px;
  font-weight: 400;
}

.invoice-doc__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.invoice-doc__label-row .invoice-doc__label {
  margin-bottom: 0;
}

.invoice-doc__value {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-color, #1f2937);
  word-break: break-word;
}
.invoice-doc__value--muted {
  font-weight: 400;
  color: var(--grey-darkest, #6b7280);
}

.invoice-doc__input-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.invoice-doc__patient-picker {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.invoice-doc__helper {
  margin-top: 4px;
  font-size: 13px;
  color: var(--grey-dark, #888);
}

.invoice-doc__line-items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.invoice-doc__divider {
  border: none;
  border-top: 1px solid var(--grey-light, #e5e7eb);
  margin: 16px 0;
}
.invoice-doc__divider--section {
  margin: 24px 0;
}

.invoice-doc__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.invoice-doc__th {
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: var(--grey-darkest, #6b7280);
  padding: 8px 0;
  border-bottom: 1px solid var(--grey-light, #e5e7eb);
}
.invoice-doc__th--right {
  text-align: right;
}
.invoice-doc__th--center {
  text-align: center;
}
.invoice-doc__th--actions {
  width: 40px;
}

.invoice-doc__td {
  padding: 10px 0;
  color: var(--text-color, #1f2937);
  border-bottom: 1px solid var(--grey-lightest, #f3f4f6);
}
.invoice-doc__td--right {
  text-align: right;
}
.invoice-doc__td--center {
  text-align: center;
}
.invoice-doc__td--actions {
  text-align: center;
  width: 40px;
}

.invoice-doc__th + .invoice-doc__th,
.invoice-doc__td + .invoice-doc__td {
  padding-left: 16px;
}

.invoice-doc__tfoot-empty {
  border: none;
  padding: 0;
}

.invoice-doc__tfoot-label {
  text-align: right;
  padding: 8px 0;
  border: none;
  color: var(--grey-darkest, #6b7280);
  border-top: 1px solid var(--grey-light, #e5e7eb);
}
.invoice-doc__tfoot-label--total {
  font-weight: 700;
  color: var(--text-color, #1f2937);
}

.invoice-doc__tfoot-value {
  text-align: right;
  padding: 8px 0 8px 16px;
  border: none;
  border-top: 1px solid var(--grey-light, #e5e7eb);
}
.invoice-doc__tfoot-value--total {
  font-weight: 700;
  font-size: 18px;
  color: var(--text-color, #1f2937);
}

.invoice-doc__empty {
  text-align: center;
  color: #888;
  padding: 24px 0;
}

.invoice-doc__errors {
  background: var(--status-red, #dc2626);
  color: #fff;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 1.5;
}
.invoice-doc__error {
  margin: 0;
}
.invoice-doc__error + .invoice-doc__error {
  margin-top: 4px;
}
</style>
